import React, { useEffect } from 'react';
import {
  Download,
  AlertTriangle,
  Filter,
} from 'lucide-react';
import FilterPanel from './FilterPanel';
import DistrictsTable from './DistrictsTable';
import MandalsTable from './MandalsTable';
import { masterDataAPI } from '../../services/master-data-api.service';
import { useDistrictsData } from '../../hooks/useDistrictsData';
import { DistrictFilters, EnhancedDistrictStats } from '../../types/districts.types';
import { formatNumber } from '../../utils/districts.utils';

const DistrictsList: React.FC = () => {
    const {
    districts,
    mandals,
    summary,
    loading,
    mandalsLoading,
    error,
    fetchDistricts,
    fetchMandals,
    clearError,
    resetMandals,
  } = useDistrictsData();

  const [filters, setFilters] = React.useState<DistrictFilters>({
    sortBy: 'name',
    sortOrder: 'ASC',
  });
  const [isFilterExpanded, setIsFilterExpanded] = React.useState<boolean>(false);
  const [selectedDistrict, setSelectedDistrict] = React.useState<EnhancedDistrictStats | null>(null);

  // Calculate active filters count
  const activeFiltersCount = React.useMemo(() => {
    return Object.entries(filters).reduce((count, [key, value]) => {
      if (['sortBy', 'sortOrder'].includes(key)) return count;
      return value !== undefined && value !== '' ? count + 1 : count;
    }, 0);
  }, [filters]);

  // Fetch districts on mount and filter change
  useEffect(() => {
    fetchDistricts(filters);
  }, [fetchDistricts, filters]);

  // Handle filter changes
  const handleFiltersChange = (newFilters: DistrictFilters) => {
    setFilters(newFilters);
  };

  // Handle reset filters
  const handleResetFilters = () => {
    setFilters({ sortBy: 'name', sortOrder: 'ASC' });
  };

  // Handle sort
  const handleSort = (field: keyof EnhancedDistrictStats) => {
    const newSortOrder = filters.sortBy === field && filters.sortOrder === 'ASC' ? 'DESC' : 'ASC';
    setFilters({ ...filters, sortBy: field, sortOrder: newSortOrder });
  };

  // Handle district click
  const handleDistrictClick = (district: EnhancedDistrictStats) => {
    setSelectedDistrict(district);
    fetchMandals(district.id);
  };

  // Handle back from mandals view
  const handleBack = () => {
    setSelectedDistrict(null);
    resetMandals();
  };

    function setError(arg0: string) {
        throw new Error('Function not implemented.');
    }

  // Render
  return (
    <div className="container mx-auto p-6">
      {/* Error Display */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
          <span>{error}</span>
          <button
            onClick={clearError}
            className="absolute top-0 right-0 px-4 py-3"
            aria-label="Close error message"
          >
            X
          </button>
        </div>
      )}

      {/* Header */}
      {!selectedDistrict && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Districts Overview</h1>
            <p className="text-gray-600">
              {summary.total_districts} districts • {formatNumber(summary.total_works)} total works
            </p>
          </div>
          <button
            onClick={async () => {
              try {
                await masterDataAPI.downloadExportData(`districts_export_${new Date().toISOString().split('T')[0]}.json`);
              } catch (error) {
                setError(error instanceof Error ? error.message : 'Failed to export data');
              }
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            aria-label="Export districts data"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Districts
          </button>
        </div>
      )}

      {/* Filter Panel */}
      {!selectedDistrict && (
        <FilterPanel
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onReset={handleResetFilters}
          isExpanded={isFilterExpanded}
          onToggle={() => setIsFilterExpanded(!isFilterExpanded)}
          activeFiltersCount={activeFiltersCount}
        />
      )}

      {/* Main Content */}
      {selectedDistrict ? (
        <MandalsTable
          mandals={mandals}
          loading={mandalsLoading}
          onBack={handleBack}
          selectedDistrict={selectedDistrict}
        />
      ) : (
        <DistrictsTable
          districts={districts}
          loading={loading}
          onDistrictClick={handleDistrictClick}
          sortBy={filters.sortBy || 'name'}
          sortOrder={filters.sortOrder || 'ASC'}
          onSort={handleSort}
        />
      )}
    </div>
  );
};

export default DistrictsList;
